/**
 * 📺 BIG SCREEN - Stadium Display
 * Shows on giant screens at entrances
 * Displays QR code for fans to scan!
 * 
 * Mystery preview that REVEALS after event!
 */

import { useState, useEffect } from 'react';
import { Smartphone, Sparkles, Users, Eye, Lock } from 'lucide-react';
import styles from './BigScreen.module.css';

export function BigScreenPage() {
  const [scanCount, setScanCount] = useState(12847);
  const [showPulse, setShowPulse] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [revealing, setRevealing] = useState(false);

  // Simulate live scan counter
  useEffect(() => {
    const interval = setInterval(() => {
      setScanCount(prev => prev + Math.floor(Math.random() * 3) + 1);
      setShowPulse(true);
      setTimeout(() => setShowPulse(false), 300);
    }, 2000 + Math.random() * 3000);
    return () => clearInterval(interval);
  }, []);

  // Listen for reveal event from admin (both CustomEvent and BroadcastChannel)
  useEffect(() => {
    const handleReveal = () => {
      setRevealing(true);
      // Dramatic reveal animation
      setTimeout(() => {
        setRevealed(true);
        setRevealing(false);
      }, 2000);
    };

    // Listen for local CustomEvent
    window.addEventListener('pixel-reveal', handleReveal);
    
    // Listen for BroadcastChannel from other tabs
    const channel = new BroadcastChannel('people-pixels');
    channel.onmessage = (event) => {
      if (event.data.type === 'REVEAL') {
        handleReveal();
      }
    };

    return () => {
      window.removeEventListener('pixel-reveal', handleReveal);
      channel.close();
    };
  }, []);

  // Manual reveal for testing (click mystery box 3 times)
  const [clickCount, setClickCount] = useState(0);
  const handleMysteryClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount >= 3) {
      setRevealing(true);
      setTimeout(() => {
        setRevealed(true);
        setRevealing(false);
      }, 2000);
    }
  };

  // Generate QR code URL
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent('https://frosty-flow.pages.dev/entry')}&bgcolor=1a1a2e&color=FFD200`;

  return (
    <div className={styles.container}>
      {/* Animated background */}
      <div className={styles.bgPattern} />
      
      {/* Header */}
      <header className={styles.header}>
        <Sparkles size={48} className={styles.headerIcon} />
        <h1>PEOPLE PIXELS</h1>
        <p>Be Part of Tonight's Show!</p>
      </header>

      {/* Main Content */}
      <div className={styles.main}>
        {/* QR Code */}
        <div className={styles.qrSection}>
          <div className={styles.qrContainer}>
            <img 
              src={qrUrl} 
              alt="Scan to join People Pixels"
              className={styles.qrCode}
            />
            <div className={styles.qrGlow} />
          </div>
          <div className={styles.qrLabel}>
            <Smartphone size={32} />
            <span>SCAN WITH YOUR PHONE!</span>
          </div>
        </div>

        {/* Features */}
        <div className={styles.features}>
          <div className={styles.feature}>
            <div className={styles.featureIcon}>🎨</div>
            <div className={styles.featureText}>
              <h3>YOUR PHONE = ONE PIXEL</h3>
              <p>Help create amazing crowd displays!</p>
            </div>
          </div>
          
          <div className={styles.feature}>
            <div className={styles.featureIcon}>🍦</div>
            <div className={styles.featureText}>
              <h3>ORDER FOOD FAST</h3>
              <p>Skip the queue, delivered to your seat!</p>
            </div>
          </div>
          
          <div className={styles.feature}>
            <div className={styles.featureIcon}>🎁</div>
            <div className={styles.featureText}>
              <h3>WIN REWARDS</h3>
              <p>Participate = Discounts & Prizes!</p>
            </div>
          </div>
          
          <div className={styles.feature}>
            <div className={styles.featureIcon}>📸</div>
            <div className={styles.featureText}>
              <h3>GET THE DRONE PHOTO</h3>
              <p>Shareable keepsake sent to you!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Live Counter */}
      <div className={styles.counter}>
        <Users size={32} />
        <div className={styles.counterText}>
          <span className={`${styles.counterValue} ${showPulse ? styles.pulse : ''}`}>
            {scanCount.toLocaleString()}
          </span>
          <span className={styles.counterLabel}>FANS READY</span>
        </div>
        <div className={styles.counterDot} />
      </div>

      {/* Bottom Instructions */}
      <div className={styles.instructions}>
        <div className={styles.step}>
          <span className={styles.stepNumber}>1</span>
          <span>Open camera</span>
        </div>
        <div className={styles.stepArrow}>→</div>
        <div className={styles.step}>
          <span className={styles.stepNumber}>2</span>
          <span>Scan QR code</span>
        </div>
        <div className={styles.stepArrow}>→</div>
        <div className={styles.step}>
          <span className={styles.stepNumber}>3</span>
          <span>You're in!</span>
        </div>
      </div>

      {/* Preview - Mystery or Revealed */}
      <div 
        className={`${styles.preview} ${revealing ? styles.previewRevealing : ''} ${revealed ? styles.previewRevealed : ''}`}
        onClick={!revealed ? handleMysteryClick : undefined}
      >
        {!revealed ? (
          // MYSTERY STATE - shaking ???
          <div className={styles.mystery}>
            <div className={styles.mysteryIcon}>
              <Lock size={48} />
            </div>
            <div className={styles.mysteryText}>???</div>
            <div className={styles.mysteryHint}>
              {revealing ? '✨ REVEALING... ✨' : 'TOP SECRET!'}
            </div>
          </div>
        ) : (
          // REVEALED STATE - show the image!
          <>
            <div className={styles.revealBurst}>🎉</div>
            <img src="/peoplepixels.jpg" alt="Tonight's Pixel Art" />
            <div className={styles.previewLabelRevealed}>
              <Eye size={20} />
              <span>TONIGHT'S PIXEL ART!</span>
            </div>
          </>
        )}
        <div className={styles.previewLabel}>
          {revealed ? "WE DID IT!" : "TONIGHT'S ART"}
        </div>
      </div>
    </div>
  );
}
